import { io, type Socket } from "socket.io-client";
import {
  emitTransportEvent,
  type PlayerIdentity,
  SERVER_EVENTS,
  type SignalingTransport,
} from "./SignalingTransport";

interface SocketReply {
  ok: boolean;
  error?: string;
  lobby?: unknown;
  requestId?: string;
}

export class SocketIoTransport extends EventTarget implements SignalingTransport {
  private socket: Socket | null = null;
  private intentionalClose = false;

  constructor(private readonly url: string, private readonly lanMode = false) {
    super();
  }

  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  get sessionToken(): string | null {
    return null;
  }

  async connect(identity: PlayerIdentity): Promise<void> {
    if (this.connected) return;
    this.intentionalClose = false;
    this.socket?.disconnect();
    const socket = io(this.url, {
      auth: identity,
      transports: ["websocket", "polling"],
      // Do not let a rejected or rate-limited client reconnect forever in the
      // background. The UI can retry after the user sees the error.
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 4_000,
      timeout: 8_000,
    });
    this.socket = socket;
    this.bindEvents(socket);
    await new Promise<void>((resolve, reject) => {
      const accept = (): void => {
        socket.off("connect_error", fail);
        resolve();
      };
      const fail = (error: Error): void => {
        reject(this.lanMode
          ? new Error(`LAN host unavailable at ${this.url}. Start the LAN host and allow port 3001 through its firewall.`)
          : error);
      };
      socket.once("connect", accept);
      socket.once("connect_error", fail);
    });
  }

  setIdentity(identity: PlayerIdentity): void {
    if (!this.socket) return;
    this.socket.auth = identity;
    if (this.connected) this.notify("identity:update", identity);
  }

  request<T>(event: string, data: object): Promise<T> {
    if (!this.socket?.connected) return Promise.reject(new Error("The signaling connection is not ready."));
    return new Promise((resolve, reject) => {
      this.socket!.emit(event, data, (reply: unknown) => {
        if (event === "lobby:list") return resolve(reply as T);
        const result = reply as SocketReply;
        if (!result?.ok) return reject(new Error(result?.error || "The signaling request failed."));
        if ("lobby" in result) return resolve(result.lobby as T);
        if ("requestId" in result) return resolve({ requestId: result.requestId } as T);
        resolve(undefined as T);
      });
    });
  }

  notify(event: string, data: object): void {
    this.socket?.emit(event, data);
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.socket?.disconnect();
    this.socket = null;
  }

  private bindEvents(socket: Socket): void {
    for (const name of SERVER_EVENTS) {
      socket.on(name, (detail: unknown) => emitTransportEvent(this, name, detail));
    }
    socket.on("disconnect", () => {
      if (!this.intentionalClose) emitTransportEvent(this, "offline", null);
    });
  }
}
